import {AnyAttrs, AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {DEFAULT_CAP, PlanBuilderState} from 'media/js/pricing/plan-builder-2023/types';
import {BillingFrequency, PlanState, PricingCurveVariant, SliderNotch} from 'media/js/pricing/api/types';
import type {PlanBuilder} from 'media/js/pricing/plan-builder-2023/index';
import {TrackingEvents, TrackingPropertyNames} from 'media/js/pricing/tracking';
import {abbreviateNumber, commaizeNumber} from 'common/util';

import './index.styl';
import {HARDCODED_TICK_WIDTH_LIMIT, MtuSliderState, SliderNotchSize} from './types';
import {template} from './template';
import css from './index.inline.styl';

export class PlanBuilderStepSlider25k extends Component<MtuSliderState, AnyAttrs, PlanBuilderState, PlanBuilder> {
  static get attrsSchema(): AttrsSchema<AnyAttrs> {
    return {
      ...super.attrsSchema,
      'mp-2023-style': {
        type: `boolean`,
        description: `flag to check whether page should apply new 2023 stying`,
      },
      'is-reverse-trial': {
        type: `boolean`,
        description: `flag to check whether the user is on a reverse trial`,
      },
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        currentValue: null,
        isDragging: false,
        dragClientX: 0,
        sliderPartRects: null,
        disableContinue: false,
        enterpriseCapStart: 0,
        enterpriseCapEnd: 0,
        growthCapStart: 0,
        growthCapEnd: 0,
        isFaqModalOpen: false,
        growthPriceStart: 0,
        enterprisePriceStart: 0,
      },

      helpers: {
        handleSliderPartClick: (notch) => {
          this.helpers.setCurrentValue(notch.value);
        },

        handleDragThumbMouseDown: (ev) => {
          document.body.addEventListener(`mouseup`, this.helpers.handleBodyMouseUp);
          document.body.addEventListener(`touchend`, this.helpers.handleBodyMouseUp);
          document.body.addEventListener(`mousemove`, this.helpers.handleBodyMouseMove);
          document.body.addEventListener(`touchmove`, this.helpers.handleBodyMouseMove);
          const dragClientX = ev.clientX || (ev.touches && ev.touches[0].clientX);
          this.update({dragClientX, isDragging: true});
          if (this.app.state.showCurrentPlanPane) {
            // These customers should get a loader as they will be seeing the breakdown on this page
            this.app.update({isAmountDueInfoLoading: true});
          }
          this.app.update({dontUpdateAmountDue: true});
        },

        handleBodyMouseUp: () => {
          document.body.removeEventListener(`mousemove`, this.helpers.handleBodyMouseMove);
          document.body.removeEventListener(`touchmove`, this.helpers.handleBodyMouseMove);
          document.body.removeEventListener(`mouseup`, this.helpers.handleBodyMouseUp);
          document.body.removeEventListener(`touchend`, this.helpers.handleBodyMouseUp);
          this.update({isDragging: false});
          this.app.update({dontUpdateAmountDue: false});
          if (this.app.state.showCurrentPlanPane) {
            // We only show breakdown on page 1 and 2 for those customers with "current plan" pane
            this.app.helpers.handleUpdateBreakdown();
          }
        },

        handleBodyMouseMove: (ev) => {
          const dragClientX = ev.clientX || (ev.touches && ev.touches[0].clientX);
          this.update({dragClientX});
        },

        handleWindowResize: () => {
          this.update();
        },

        getThumbTransform: () => {
          const {isDragging, dragClientX, currentValue} = this.state;
          const sliderPartRects = this.helpers.getSliderPartRects();
          let thumbX = 0;

          if (sliderPartRects && sliderPartRects.length) {
            const sliderRectLeft = sliderPartRects[0].left;
            const sliderRectRight = sliderPartRects[sliderPartRects.length - 1].right;
            const sliderData = this.helpers.getSliderData();

            if (isDragging) {
              // if user is draggging, then we show the thumb exactly where their mouse is
              const clampedX = Math.min(Math.max(sliderRectLeft, dragClientX), sliderRectRight);
              thumbX = clampedX - sliderRectLeft;
              // find which notch the thumb is on and select that value
              const notchIndex = sliderPartRects.findIndex((r) => clampedX >= r.left && clampedX <= r.right);
              if (notchIndex >= 0) {
                // raf so we don't apply an update while render is in progress
                requestAnimationFrame(() => this.helpers.setCurrentValue(sliderData[notchIndex].value));
              }
            } else {
              // if user is not dragging, then we show the thumb at the selected notch
              const notchIndex = sliderData.findIndex((n) => n.value === currentValue);
              if (notchIndex >= 0 && notchIndex < sliderPartRects.length) {
                thumbX = sliderPartRects[notchIndex].right - sliderRectLeft;
              } else {
                requestAnimationFrame(() => this.helpers.setCurrentValue(sliderData[0].value));
              }
            }
          }

          return `translateX(${thumbX - 12}px)`; // - offset for centering
        },

        getAboveMaxNotch: (sliderData: SliderNotch[]): SliderNotch => {
          const largestValue = sliderData[sliderData.length - 1];
          return {
            label: `${largestValue.label}+`,
            alwaysShowLabel: false,
            value: largestValue.value + 1,
            notchSize: SliderNotchSize.SMALL,
          };
        },

        getSliderData: (): SliderNotch[] => {
          /**
           * Enterprise and growth belong on different curves but have overlapping cap values (from 5m to 100m).
           * This manuever is only possible due to growth annual/monthly having the exact same slider configuration.
           * If any changes causes the slider to break, it is likely due to some deviation of growth annual and monthly slider data.
           */
          const growth = this.app.helpers.getPricingCurveWithParams(
            BillingFrequency.ANNUAL,
            PricingCurveVariant.GROWTH,
          );

          return this.helpers.populateNotchWidth([
            ...growth.sliderInfo,
            this.helpers.getAboveMaxNotch(growth.sliderInfo),
          ]);
        },

        populateNotchWidth: (sliderData): SliderNotch[] => {
          return sliderData.map((curvePoint, i) => {
            const lastValue = i ? sliderData[i - 1].value : 0;
            return {...curvePoint, width: Math.min((curvePoint.value - lastValue) / 2, HARDCODED_TICK_WIDTH_LIMIT)};
          });
        },

        setCurrentValue: (currentValue) => {
          const sliderData = this.helpers.getSliderData();
          this.update({
            disableContinue: currentValue === sliderData[sliderData.length - 1].value,
          });

          if (currentValue !== this.state.currentValue) {
            this.update({currentValue});
            const planChange: Partial<PlanState> = {cap: currentValue};

            this.app.helpers.handleChangeSelectedPlan(planChange);
            this.app.helpers.trackEventWithPlanProps(TrackingEvents.SliderCapUpdate, planChange);
          }
        },

        getCurrentNotch: () => {
          return this.helpers.getSliderData().find((n) => n.value === this.state.currentValue);
        },

        getSliderPartRects: () => {
          return Array.from(this.el.querySelectorAll(`.mtu-slider-part`)).map((el) => el.getBoundingClientRect());
        },

        handleContinue: () => {
          this.app.helpers.goToNextStep();
        },

        getUsageAmount: () => {
          return this.app.state.customerInfo?.orgInfo?.mtuUsage || null;
        },

        getUsageHref: () => {
          return `/settings/org/${this.app.state.customerInfo.orgInfo.orgId}/plan/detail/events`;
        },

        getRecommendedUsageAmount: () => {
          const {planRecommendation} = this.app.state;
          if (planRecommendation && planRecommendation.events_usage) {
            return planRecommendation.events_usage;
          }
          return null;
        },

        showUsageCallout: () => {
          return this.app.state.customerInfo?.isAuthenticated && this.helpers.getRecommendedUsageAmount();
        },

        getPlanRecommendationInfo: () => {
          const {planRecommendation} = this.app.state;
          if (!planRecommendation || !planRecommendation.cap) {
            return null;
          }
          const basedOn = planRecommendation.is_reverse_trial
            ? `the traffic during your trial`
            : `last month's traffic`;
          return `We suggest a ${this.app.helpers.abbreviateNumber(
            planRecommendation.cap,
          )} Events plan, based on ${basedOn}.`;
        },

        sortNumStringsAsc: (a, b) => parseInt(a) - parseInt(b),

        getFirstPointOfAnnualCurve: (variant) => {
          const points = this.app.helpers.getPricingCurveWithParams(BillingFrequency.ANNUAL, variant).points;
          const first = Object.keys(points).sort(this.helpers.sortNumStringsAsc)[0]; // should pick up lowest annual cost point for either enterprise or growth
          return {
            price: commaizeNumber(points[first].price),
            cap: abbreviateNumber(points[first].cap, {precision: 0}),
            raw_price: points[first].price,
          };
        },

        setGrowthCapLimits: () => {
          const growthPoints = this.app.helpers.getPricingCurveWithParams(
            BillingFrequency.MONTHLY,
            PricingCurveVariant.GROWTH,
          ).points;
          const growthKeys = Object.keys(growthPoints).sort(this.helpers.sortNumStringsAsc);
          const growthFirst = growthKeys[0];
          const growthLast = growthKeys[growthKeys.length - 1];
          this.update({
            growthCapStart: growthPoints[growthFirst].cap,
            growthCapEnd: growthPoints[growthLast].cap,
          });
        },
        setStartingPrice: () => {
          const growthPrice = this.helpers.getFirstPointOfAnnualCurve(PricingCurveVariant.GROWTH).raw_price;
          this.update({
            growthPriceStart: Math.floor(growthPrice / BillingFrequency.ANNUAL),
          });
        },
        //TODO(p2023 GOOT): remove this function after launch
        shouldDisableGrowthCard: (variant) => {
          if (this.app.helpers.isP2023SeptGootEnabled()) return false;
          return (
            variant === PricingCurveVariant.GROWTH &&
            this.app.helpers.getSelectedPlanState().cap > this.state.growthCapEnd
          ); // disable growth if over 100M events
        },
        handleFaqModal: (ev) => {
          if (ev.detail.state === `closed`) {
            this.update({isFaqModalOpen: false});
          } else {
            this.update({isFaqModalOpen: true});
          }
        },
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();

    // remove breakdown
    this.app.helpers.handleUpdateBreakdown(true);

    // after the modal opens, re-render with sliderPart rects in correct position
    requestAnimationFrame(() => this.update());
    window.addEventListener(`resize`, this.helpers.handleWindowResize);

    this.helpers.setGrowthCapLimits(); // will set breakpoints for growth (should be 100m for growth)
    this.helpers.setStartingPrice();
    const currentValue = this.app.helpers.getSelectedPlanState()?.cap || DEFAULT_CAP;
    this.helpers.setCurrentValue(currentValue);

    this.app.helpers.trackEventWithPlanProps(TrackingEvents.VariantSelected, {
      [TrackingPropertyNames.CurveVariant]: this.app.helpers.getSelectedPlanState()?.curveVariant,
    });
  }

  public disconnectedCallback() {
    super.disconnectedCallback();
    document.body.removeEventListener(`mousemove`, this.helpers.handleBodyMouseMove);
    document.body.removeEventListener(`touchmove`, this.helpers.handleBodyMouseMove);
    window.removeEventListener(`resize`, this.helpers.handleWindowResize);
  }
}

export const PlanBuilderStepSlider25K = defineAndMakeJsxFactory(
  `plan-builder-step-slider-25k-2023`,
  PlanBuilderStepSlider25k,
);
